import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { LoadingView, SportsBackground } from "@/components/ui";
import {
  activeLeagueAdminMembership,
  activeLeagueManagementMembership,
  activeOrganizationAdminMembership,
  activeMembership,
  canShowAITab,
  canShowHomeDashboardCard,
  isMembershipCurrentlyActive,
  needsOnboarding,
  resolvedLeagueID,
  type HomeDashboardCardID,
} from "@/models/user";
import { actionAuditScopes, canReadBroadcastOverlays } from "@/models/featureAccessPolicy";
import { useAuth } from "./AuthProvider";

// UX-level route guards mirroring the SBotUser canManage* checks. The real
// authorization boundary is firestore.rules — these only shape navigation.

export function HoldScreen() {
  return (
    <SportsBackground>
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <LoadingView label="Loading SBot…" />
      </div>
    </SportsBackground>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isResolvingSession } = useAuth();
  if (isResolvingSession) return <HoldScreen />;
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

export function RequireOnboarded({ children }: { children: ReactNode }) {
  const { currentUser, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (needsOnboarding(currentUser)) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

/**
 * League managers/admins OR organization admins (who carry the "admin" role in
 * their league member summary, which firestore.rules' canManageLeagueData
 * accepts). External Data intentionally retains this broader legacy gate;
 * Broadcast Overlays use RequireBroadcastOverlayAccess below.
 */
export function RequireLeagueDataAccess({ children }: { children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (
    !isSBotLabsAdmin &&
    !activeLeagueManagementMembership(currentUser) &&
    !activeOrganizationAdminMembership(currentUser)
  ) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

/** Broadcast Overlay card holders may inspect; Firestore and the UI keep writes manager-only. */
export function RequireBroadcastOverlayAccess({ children }: { children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (isSBotLabsAdmin) return <>{children}</>;
  const membership = activeMembership(currentUser);
  const leagueID = membership ? resolvedLeagueID(membership) : undefined;
  if (!leagueID || !canReadBroadcastOverlays(currentUser, isSBotLabsAdmin, leagueID)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

export function RequireLeagueManagement({ children }: { children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (!isSBotLabsAdmin && !activeLeagueManagementMembership(currentUser)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

/** Staff detail mirrors iOS' league-scoped profile gate across all current memberships. */
export function RequireStaffProfileAccess({ children }: { children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (isSBotLabsAdmin) return <>{children}</>;

  const workspaceMembership = activeMembership(currentUser);
  const leagueID = workspaceMembership ? resolvedLeagueID(workspaceMembership) : undefined;
  const canInspectStaff = !!leagueID && currentUser.memberships.some((membership) =>
    isMembershipCurrentlyActive(membership)
      && membership.targetType === "league"
      && resolvedLeagueID(membership) === leagueID
      && (membership.role === "admin" || membership.role === "leagueManager"),
  );
  if (!canInspectStaff) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

export function RequireLeagueAdmin({ children }: { children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (!isSBotLabsAdmin && !activeLeagueAdminMembership(currentUser)) {
    return <Navigate to="/league" replace />;
  }
  return <>{children}</>;
}

export function RequireAIAccess({ children }: { children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (!canShowAITab(currentUser, isSBotLabsAdmin)) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

/** Activity is available only when at least one Firestore-provable scope exists in the active league. */
export function RequireActivityAccess({ children }: { children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  const membership = activeMembership(currentUser);
  const leagueID =
    currentUser.activeContext?.leagueID
    ?? (currentUser.activeContext?.targetType === "league" ? currentUser.activeContext.targetID : undefined)
    ?? (membership ? resolvedLeagueID(membership) : undefined);
  if (actionAuditScopes(currentUser, isSBotLabsAdmin, leagueID).length === 0) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

export function RequireSBotLabsAdmin({ children }: { children: ReactNode }) {
  const { isSBotLabsAdmin, isResolvingSession, currentUser } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (!isSBotLabsAdmin) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

export function RequireHomeCard({ cardID, children }: { cardID: HomeDashboardCardID; children: ReactNode }) {
  const { currentUser, isSBotLabsAdmin, isResolvingSession } = useAuth();
  if (isResolvingSession || !currentUser) return <HoldScreen />;
  if (!isSBotLabsAdmin && !canShowHomeDashboardCard(cardID, currentUser)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}
